// Kernvel-staff gate for the /internal/* pages (never an AdminUser session, never in the nav).
//
// Asks for a staff token (minted offline by Kernvel), verifies it against GET /api/v1/staff/session
// (which the backend audits), then renders the page with the verified identity and scopes. A
// refused/expired token is dropped and the prompt comes back.
import { useCallback, useEffect, useState, type ReactNode } from "react";

import { getStaffToken, setStaffToken, staffErrorText, staffFetch, staffPost } from "../staff";
import type { StaffScope, StaffSession } from "../types";

export default function StaffGate({
  title,
  scope,
  children,
}: {
  title: string;
  scope: StaffScope;
  children: (session: StaffSession) => ReactNode;
}) {
  const [session, setSession] = useState<StaffSession | null>(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const verify = useCallback(async (token: string) => {
    setBusy(true);
    setErr(null);
    try {
      const verified = await staffFetch<StaffSession>("/session", { method: "GET" }, token);
      setStaffToken(token);
      setSession(verified);
    } catch (e) {
      setStaffToken("");
      setSession(null);
      setErr(staffErrorText(e, "Could not verify the staff token"));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const stored = getStaffToken();
    if (stored) void verify(stored);
  }, [verify]);

  const signOut = useCallback(async () => {
    try {
      await staffPost("/session/logout", {});
    } catch {
      /* already refused: nothing to revoke */
    }
    setStaffToken("");
    setSession(null);
  }, []);

  if (!session) {
    return (
      <div className="board-page">
        <div className="board-header">
          <h1>{title} (Kernvel staff)</h1>
        </div>
        <p className="risk-sub">
          Kernvel-internal — never part of any merchant's console. Paste a staff token minted with
          scripts/kernvel_staff_issuer.py; it is kept in this tab only.
        </p>
        <div className="card" style={{ marginBottom: 16 }}>
          <label>
            Staff token
            <input
              type="password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 4 }}
            />
          </label>
          <button onClick={() => void verify(draft.trim())} disabled={busy || !draft.trim()} style={{ marginTop: 8 }}>
            {busy ? "Verifying…" : "Verify"}
          </button>
          {err && <p className="risk-sub">Error: {err}</p>}
        </div>
      </div>
    );
  }
  const expires = new Date(session.expires_at * 1000).toISOString().replace("T", " ").slice(0, 19);
  return (
    <div className="board-page">
      <div className="board-header">
        <h1>{title} (Kernvel staff)</h1>
      </div>
      <p className="risk-sub">
        Signed in as {session.display_name || session.staff_ref} · scopes: {session.scopes.join(", ") || "none"} ·
        token expires {expires} UTC ·{" "}
        <button className="courier-btn" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
      {session.scopes.includes(scope) ? (
        children(session)
      ) : (
        <p className="cap-note">Your staff grant does not include {scope}.</p>
      )}
    </div>
  );
}
